import React, { useState } from 'react';
import { StudentLayout } from '../layouts/StudentLayout';
import { CreditCardIcon, PlusIcon, TrashIcon, LockIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { showToast } from '../ToastContainer';
interface PaymentMethod {
  id: string;
  type: 'card' | 'paypal' | 'bank';
  name: string;
  last4: string;
  expiryMonth?: string;
  expiryYear?: string;
  isDefault: boolean;
  brand?: string;
}
export const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([{
    id: '1',
    type: 'card',
    name: 'Sarah Johnson',
    last4: '4242',
    expiryMonth: '12',
    expiryYear: '2025',
    isDefault: true,
    brand: 'visa'
  }, {
    id: '2',
    type: 'paypal',
    name: 'sarah.johnson@example.com',
    last4: '',
    isDefault: false
  }]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newCard, setNewCard] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });
  // Handle input change for new card form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      setNewCard({
        ...newCard,
        [name]: formatted
      });
      return;
    }
    setNewCard({
      ...newCard,
      [name]: value
    });
  };
  // Set a payment method as default
  const setAsDefault = (id: string) => {
    setPaymentMethods(paymentMethods.map(method => ({
      ...method,
      isDefault: method.id === id
    })));
    showToast.success('Default payment method updated');
  };
  // Delete a payment method
  const deletePaymentMethod = (id: string) => {
    // Prevent deleting the default payment method
    const methodToDelete = paymentMethods.find(method => method.id === id);
    if (methodToDelete?.isDefault) {
      showToast.error('Cannot delete default payment method. Please set another method as default first.');
      return;
    }
    setPaymentMethods(paymentMethods.filter(method => method.id !== id));
    showToast.success('Payment method removed');
  };
  // Add a new payment method
  const addPaymentMethod = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    // Validate form
    if (!newCard.cardNumber || !newCard.cardName || !newCard.expiryMonth || !newCard.expiryYear || !newCard.cvv) {
      showToast.error('Please fill in all fields');
      setIsProcessing(false);
      return;
    }
    // Simulate API call
    setTimeout(() => {
      const last4 = newCard.cardNumber.replace(/\s/g, '').slice(-4);
      const newMethod: PaymentMethod = {
        id: Date.now().toString(),
        type: 'card',
        name: newCard.cardName,
        last4,
        expiryMonth: newCard.expiryMonth,
        expiryYear: newCard.expiryYear,
        isDefault: paymentMethods.length === 0,
        brand: 'mastercard' // Simplified for demo
      };
      setPaymentMethods([...paymentMethods, newMethod]);
      setNewCard({
        cardNumber: '',
        cardName: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: ''
      });
      setShowAddForm(false);
      setIsProcessing(false);
      showToast.success('Payment method added successfully');
    }, 1500);
  };
  // Get card brand icon
  const getCardIcon = (brand?: string) => {
    // In a real app, you would use different icons for different card brands
    return <CreditCardIcon className="h-6 w-6 text-white" data-id="element-5360" />;
  };
  return <StudentLayout data-id="element-5361">
      <div className="max-w-4xl mx-auto" data-id="element-5362">
        <div className="mb-6" data-id="element-5363">
          <h1 className="text-2xl font-serif font-bold text-white mb-2" data-id="element-5364">
            Payment Methods
          </h1>
          <p className="text-gray-300" data-id="element-5365">
            Manage your payment methods for lessons and subscriptions
          </p>
        </div>
        <div className="bg-navy-800 rounded-xl p-6 mb-8" data-id="element-5366">
          <div className="flex justify-between items-center mb-6" data-id="element-5367">
            <h2 className="text-lg font-medium text-white" data-id="element-5368">
              Your Payment Methods
            </h2>
            <button onClick={() => setShowAddForm(!showAddForm)} className="flex items-center px-3 py-1.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg text-sm" data-id="element-5369">
              {showAddForm ? 'Cancel' : <>
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-5370" />
                  Add Method
                </>}
            </button>
          </div>
          {/* Add new payment method form */}
          {showAddForm && <div className="mb-6 p-4 border border-navy-600 rounded-lg" data-id="element-5371">
              <h3 className="text-white font-medium mb-4" data-id="element-5372">Add New Card</h3>
              <form onSubmit={addPaymentMethod} data-id="element-5373">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-5374">
                  <div data-id="element-5375">
                    <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-5376">
                      Card Number
                    </label>
                    <div className="relative" data-id="element-5377">
                      <input type="text" id="cardNumber" name="cardNumber" value={newCard.cardNumber} onChange={handleInputChange} placeholder="1234 5678 9012 3456" maxLength={19} className="w-full px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-5378" />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2" data-id="element-5379">
                        <CreditCardIcon className="h-5 w-5 text-gray-400" data-id="element-5380" />
                      </div>
                    </div>
                  </div>
                  <div data-id="element-5381">
                    <label htmlFor="cardName" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-5382">
                      Name on Card
                    </label>
                    <input type="text" id="cardName" name="cardName" value={newCard.cardName} onChange={handleInputChange} placeholder="John Smith" className="w-full px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-5383" />
                  </div>
                  <div data-id="element-5384">
                    <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-5385">
                      Expiry Date
                    </label>
                    <div className="flex space-x-2" data-id="element-5386">
                      <select id="expiryMonth" name="expiryMonth" value={newCard.expiryMonth} onChange={e => setNewCard({
                    ...newCard,
                    expiryMonth: e.target.value
                  })} className="w-1/2 px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-5387">
                        <option value="" data-id="element-5388">MM</option>
                        {Array.from({
                      length: 12
                    }, (_, i) => {
                      const month = i + 1;
                      return <option key={month} value={month.toString().padStart(2, '0')} data-id="element-5389">
                                {month.toString().padStart(2, '0')}
                              </option>;
                    })}
                      </select>
                      <select id="expiryYear" name="expiryYear" value={newCard.expiryYear} onChange={e => setNewCard({
                    ...newCard,
                    expiryYear: e.target.value
                  })} className="w-1/2 px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-5390">
                        <option value="" data-id="element-5391">YYYY</option>
                        {Array.from({
                      length: 10
                    }, (_, i) => {
                      const year = new Date().getFullYear() + i;
                      return <option key={year} value={year.toString()} data-id="element-5392">
                                {year}
                              </option>;
                    })}
                      </select>
                    </div>
                  </div>
                  <div data-id="element-5393">
                    <label htmlFor="cvv" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-5394">
                      CVV
                    </label>
                    <div className="relative" data-id="element-5395">
                      <input type="password" id="cvv" name="cvv" value={newCard.cvv} onChange={handleInputChange} placeholder="123" maxLength={4} className="w-full px-3 py-2 bg-navy-700 border border-navy-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-deep-pink" data-id="element-5396" />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2" data-id="element-5397">
                        <LockIcon className="h-4 w-4 text-gray-400" data-id="element-5398" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-400 mb-4" data-id="element-5399">
                  <LockIcon className="h-4 w-4 mr-2" data-id="element-5400" />
                  Your payment information is encrypted and secure
                </div>
                <div className="flex justify-end" data-id="element-5401">
                  <button type="submit" disabled={isProcessing} className="px-4 py-2 bg-deep-pink hover:bg-burgundy disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors" data-id="element-5402">
                    {isProcessing ? <span className="flex items-center" data-id="element-5403">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-5404">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5405"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-5406"></path>
                        </svg>
                        Processing...
                      </span> : 'Add Card'}
                  </button>
                </div>
              </form>
            </div>}
          {/* List of payment methods */}
          {paymentMethods.length > 0 ? <div className="space-y-4" data-id="element-5407">
              {paymentMethods.map(method => <div key={method.id} className="flex items-center justify-between p-4 bg-navy-700 rounded-lg" data-id="element-5408">
                  <div className="flex items-center" data-id="element-5409">
                    <div className="p-2 bg-navy-600 rounded-lg mr-4" data-id="element-5410">
                      {method.type === 'card' ? getCardIcon(method.brand) : method.type === 'paypal' ? <div className="h-6 w-6 text-blue-500 font-bold" data-id="element-5411">P</div> : <div className="h-6 w-6 text-green-500" data-id="element-5412">B</div>}
                    </div>
                    <div data-id="element-5413">
                      <div className="flex items-center" data-id="element-5414">
                        <h3 className="font-medium text-white" data-id="element-5415">
                          {method.type === 'card' ? <>
                              {method.brand?.charAt(0).toUpperCase() + method.brand?.slice(1) || 'Card'}{' '}
                              •••• {method.last4}
                            </> : method.type === 'paypal' ? <>PayPal - {method.name}</> : <>Bank Account - {method.name}</>}
                        </h3>
                        {method.isDefault && <span className="ml-2 px-2 py-0.5 bg-green-900/30 text-green-400 text-xs rounded-full" data-id="element-5416">
                            Default
                          </span>}
                      </div>
                      {method.type === 'card' && method.expiryMonth && method.expiryYear && <p className="text-sm text-gray-400 mt-1" data-id="element-5417">
                            Expires {method.expiryMonth}/{method.expiryYear}
                          </p>}
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-5418">
                    {!method.isDefault && <button onClick={() => setAsDefault(method.id)} className="px-3 py-1.5 bg-navy-600 hover:bg-navy-500 text-white rounded-lg text-sm" data-id="element-5419">
                        Set as Default
                      </button>}
                    <button onClick={() => deletePaymentMethod(method.id)} className="p-1.5 bg-navy-600 hover:bg-red-600 text-white rounded-lg" aria-label="Delete payment method" data-id="element-5420">
                      <TrashIcon className="h-4 w-4" data-id="element-5421" />
                    </button>
                  </div>
                </div>)}
            </div> : <div className="text-center py-8" data-id="element-5422">
              <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-navy-700 mb-4" data-id="element-5423">
                <CreditCardIcon className="h-8 w-8 text-gray-400" data-id="element-5424" />
              </div>
              <p className="text-gray-300 mb-4" data-id="element-5425">No payment methods added yet</p>
              <button onClick={() => setShowAddForm(true)} className="px-4 py-2 bg-deep-pink hover:bg-burgundy text-white rounded-lg" data-id="element-5426">
                Add Payment Method
              </button>
            </div>}
        </div>
        <div className="bg-navy-800 rounded-xl p-6" data-id="element-5427">
          <h2 className="text-lg font-medium text-white mb-4" data-id="element-5428">
            Payment Security
          </h2>
          <div className="space-y-4" data-id="element-5429">
            <div className="flex items-start" data-id="element-5430">
              <div className="p-2 bg-green-900/30 rounded-lg mr-3" data-id="element-5431">
                <LockIcon className="h-5 w-5 text-green-400" data-id="element-5432" />
              </div>
              <div data-id="element-5433">
                <h3 className="font-medium text-white" data-id="element-5434">Secure Encryption</h3>
                <p className="text-sm text-gray-300 mt-1" data-id="element-5435">
                  All payment information is encrypted using industry-standard
                  SSL technology.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-5436">
              <div className="p-2 bg-green-900/30 rounded-lg mr-3" data-id="element-5437">
                <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-5438" />
              </div>
              <div data-id="element-5439">
                <h3 className="font-medium text-white" data-id="element-5440">PCI Compliant</h3>
                <p className="text-sm text-gray-300 mt-1" data-id="element-5441">
                  Our payment processing meets the highest security standards
                  for handling credit card information.
                </p>
              </div>
            </div>
            <div className="flex items-start" data-id="element-5442">
              <div className="p-2 bg-green-900/30 rounded-lg mr-3" data-id="element-5443">
                <AlertCircleIcon className="h-5 w-5 text-green-400" data-id="element-5444" />
              </div>
              <div data-id="element-5445">
                <h3 className="font-medium text-white" data-id="element-5446">Fraud Protection</h3>
                <p className="text-sm text-gray-300 mt-1" data-id="element-5447">
                  Advanced fraud detection systems help protect your account
                  from unauthorized charges.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>;
};